import React, { useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, TextField, LinearProgress, Alert,
  Chip, IconButton, Tooltip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LinkIcon from '@mui/icons-material/Link';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

const API_URL = BACKEND_URL;

function BatchTranscribe({ embedded = false }) {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const email = accounts[0]?.username || '';

  const [items, setItems] = useState([]); // [{type: 'file'|'link', file?, link?, name, status, jobId, srt, fcpxml, error}]
  const [frameIoLinks, setFrameIoLinks] = useState('');
  const [language, setLanguage] = useState('nl');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // File upload
  const onDrop = useCallback((acceptedFiles) => {
    const newItems = acceptedFiles.map(file => ({
      type: 'file',
      file,
      name: file.name,
      status: 'pending',
      jobId: null,
      srt: null,
      fcpxml: null,
      error: null,
      progress: 0,
      message: ''
    }));
    setItems(prev => [...prev, ...newItems]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
      'audio/mpeg': ['.mp3'],
      'audio/wav': ['.wav'],
      'audio/x-m4a': ['.m4a']
    },
    multiple: true
  });

  // Add Frame.io links
  const addFrameIoLinks = () => {
    const links = frameIoLinks.split('\n').map(l => l.trim()).filter(l => l && l.includes('frame.io'));
    if (links.length === 0) return;

    const newItems = links.map(link => ({
      type: 'link',
      link,
      name: link.split('/').pop() || link.substring(0, 50),
      status: 'pending',
      jobId: null,
      srt: null,
      fcpxml: null,
      error: null,
      progress: 0,
      message: ''
    }));
    setItems(prev => [...prev, ...newItems]);
    setFrameIoLinks('');
  };

  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleDownload = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Poll a single job until completion
  const pollJob = async (jobId, index) => {
    return new Promise((resolve) => {
      const interval = setInterval(async () => {
        try {
          const statusUrl = `${API_URL}/Api/srt-generator/status/${jobId}?email=${encodeURIComponent(email)}`;
          const response = await authFetch(statusUrl);
          if (!response.ok) return;

          const data = await response.json();
          setItems(prev => prev.map((item, i) =>
            i === index ? { ...item, progress: data.progress || 0, message: data.message || '' } : item
          ));

          if (data.status === 'completed') {
            clearInterval(interval);
            setItems(prev => prev.map((item, i) =>
              i === index ? {
                ...item,
                status: 'completed',
                srt: data.correctedSRT || data.originalSRT || '',
                fcpxml: data.fcpxml || '',
                name: data.originalFilename || item.name,
                progress: 100
              } : item
            ));
            resolve(true);
          } else if (data.status === 'error') {
            clearInterval(interval);
            setItems(prev => prev.map((item, i) =>
              i === index ? { ...item, status: 'error', error: data.error || 'Onbekende fout', progress: 0 } : item
            ));
            resolve(false);
          }
        } catch {
          // Keep polling on network errors
        }
      }, 3000);
    });
  };

  // Process all items sequentially
  const handleStartBatch = async () => {
    if (items.length === 0 || !email) return;
    setProcessing(true);
    setError(null);

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.status === 'completed') continue; // Skip already done

      setItems(prev => prev.map((it, idx) =>
        idx === i ? { ...it, status: 'processing', progress: 0, message: 'Starten...' } : it
      ));

      try {
        const formData = new FormData();
        if (item.type === 'file' && item.file) {
          formData.append('video', item.file);
          formData.append('originalFilename', item.file.name);
        } else if (item.type === 'link' && item.link) {
          formData.append('frameIoLink', item.link);
        }

        formData.append('language', language);
        formData.append('aspectRatio', '16:9'); // Auto-detect per item
        formData.append('subtitleStyle', 'netflix');
        formData.append('subtitleSize', 'medium');
        formData.append('ellipsisStyle', 'end_only');
        formData.append('timingOffset', '0');
        formData.append('email', email);
        formData.append('isKaraoke', 'false');
        formData.append('uploadToFrameIo', 'false');

        const response = await authFetch(`${API_URL}/Api/srt-generator/generate`, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          const errText = await response.text();
          throw new Error(errText.substring(0, 200));
        }

        const data = await response.json();
        const jobId = data.jobId;

        setItems(prev => prev.map((it, idx) =>
          idx === i ? { ...it, jobId, message: 'Verwerken...' } : it
        ));

        await pollJob(jobId, i);
      } catch (err) {
        setItems(prev => prev.map((it, idx) =>
          idx === i ? { ...it, status: 'error', error: err.message } : it
        ));
      }
    }

    setProcessing(false);
  };

  const completedCount = items.filter(i => i.status === 'completed').length;
  const errorCount = items.filter(i => i.status === 'error').length;

  const handleDownloadAllSRT = () => {
    items.filter(i => i.srt).forEach(item => {
      const filename = (item.name || 'subtitle').replace(/\.[^.]+$/, '.srt');
      handleDownload(item.srt, filename);
    });
  };

  const handleDownloadAllXML = () => {
    items.filter(i => i.fcpxml).forEach(item => {
      const filename = (item.name || 'subtitle').replace(/\.[^.]+$/, '.fcpxml');
      handleDownload(item.fcpxml, filename);
    });
  };

  return (
    <Box sx={embedded ? {} : { maxWidth: 1000, mx: 'auto', p: 3 }}>
      {!embedded && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h5" fontWeight="bold">
              Batch Transcribe
            </Typography>
            <Button variant="outlined" size="small" onClick={() => navigate('/SRTGenerator')}>
              Terug naar SRT Generator
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Transcribeer meerdere video's of audio bestanden tegelijk. Per bestand wordt een SRT en FCP-XML gegenereerd.
          </Typography>
        </>
      )}

      {/* File Upload Zone */}
      <Paper
        variant="outlined"
        sx={{
          p: 3, mb: 2, textAlign: 'center', cursor: 'pointer',
          borderStyle: 'dashed', borderColor: isDragActive ? 'primary.main' : 'divider',
          backgroundColor: isDragActive ? 'action.hover' : 'transparent'
        }}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
        <Typography>
          {isDragActive ? 'Drop bestanden hier...' : 'Sleep video/audio bestanden hierheen'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          MP4, MOV, MP3, WAV, M4A — meerdere bestanden tegelijk
        </Typography>
      </Paper>

      {/* Frame.io Links Input */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LinkIcon fontSize="small" /> Frame.io links (1 per regel)
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          value={frameIoLinks}
          onChange={(e) => setFrameIoLinks(e.target.value)}
          placeholder="https://app.frame.io/player/..."
          variant="outlined"
          size="small"
          sx={{ mb: 1 }}
        />
        <Button
          size="small"
          variant="outlined"
          onClick={addFrameIoLinks}
          disabled={!frameIoLinks.trim()}
        >
          Links toevoegen
        </Button>
      </Paper>

      {/* Language + Start */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Taal</InputLabel>
          <Select value={language} label="Taal" onChange={(e) => setLanguage(e.target.value)}>
            <MenuItem value="nl">Nederlands</MenuItem>
            <MenuItem value="en">Engels</MenuItem>
            <MenuItem value="de">Duits</MenuItem>
            <MenuItem value="fr">Frans</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          size="large"
          onClick={handleStartBatch}
          disabled={items.length === 0 || processing || !email}
          startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
          sx={{ flex: 1 }}
        >
          {processing
            ? `Verwerken... (${completedCount}/${items.length})`
            : `Transcribeer ${items.length} bestand${items.length !== 1 ? 'en' : ''}`}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Items Table */}
      {items.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Bestand</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Voortgang</TableCell>
                <TableCell align="right">Acties</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={item.type === 'file' ? 'Upload' : 'Frame.io'}
                        size="small"
                        color={item.type === 'file' ? 'default' : 'primary'}
                        variant="outlined"
                      />
                      <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                        {item.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {item.status === 'pending' && <Chip label="Wachtend" size="small" />}
                    {item.status === 'processing' && (
                      <Chip icon={<HourglassEmptyIcon />} label={item.message || 'Verwerken...'} size="small" color="warning" />
                    )}
                    {item.status === 'completed' && (
                      <Chip icon={<CheckCircleIcon />} label="Klaar" size="small" color="success" />
                    )}
                    {item.status === 'error' && (
                      <Tooltip title={item.error || 'Fout'}>
                        <Chip icon={<ErrorIcon />} label="Fout" size="small" color="error" />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    {item.status === 'processing' && (
                      <LinearProgress variant="determinate" value={item.progress} sx={{ height: 6, borderRadius: 3 }} />
                    )}
                    {item.status === 'completed' && (
                      <Typography variant="caption" color="success.main">100%</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      {item.srt && (
                        <Tooltip title="Download SRT">
                          <IconButton size="small" onClick={() => handleDownload(item.srt, item.name.replace(/\.[^.]+$/, '.srt'))}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {item.fcpxml && (
                        <Tooltip title="Download FCP-XML">
                          <IconButton size="small" color="primary" onClick={() => handleDownload(item.fcpxml, item.name.replace(/\.[^.]+$/, '.fcpxml'))}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {item.status === 'pending' && (
                        <IconButton size="small" onClick={() => removeItem(index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Batch download buttons */}
      {completedCount > 1 && (
        <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadAllSRT}>
            Download alle SRT ({completedCount})
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadAllXML}>
            Download alle FCP-XML ({completedCount})
          </Button>
        </Box>
      )}

      {/* Summary */}
      {!processing && completedCount > 0 && (
        <Alert severity={errorCount > 0 ? 'warning' : 'success'} sx={{ mt: 2 }}>
          {completedCount} van {items.length} bestanden succesvol getranscribeerd
          {errorCount > 0 && `, ${errorCount} mislukt`}
        </Alert>
      )}
    </Box>
  );
}

export default BatchTranscribe;
